import type { ComplianceCalendarItem } from "@services/facilityCompliance";
import type { DashboardCalendarEvent } from "@services/facilityDashboard";
import { parseIsoDateOnlyAsLocalDate } from "@utils/formatUtils";
import { isTaxComplianceCalendarItem } from "@utils/penaltyCalendarTax";

export function formatLocalDateIso(d: Date): string {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function isOngoingCalendarItem(item: ComplianceCalendarItem): boolean {
  return item.cadence === "ongoing" || item.deadlineType === "none";
}

/** Parse compliance calendar deadline to local YYYY-MM-DD (matches Penalty tab semantics). */
export function complianceCalendarDeadlineIso(deadline: string): string | null {
  const trimmed = deadline.trim();
  if (!trimmed || trimmed === "TBD") return null;
  const exact = parseIsoDateOnlyAsLocalDate(trimmed);
  if (exact) return formatLocalDateIso(exact);
  const head = trimmed.match(/^(\d{4}-\d{2}-\d{2})/);
  if (!head) return null;
  const fromHead = parseIsoDateOnlyAsLocalDate(head[1]);
  return fromHead ? formatLocalDateIso(fromHead) : null;
}

function startOfLocalDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function buildComplianceCalendarCountsByDate(
  items: ComplianceCalendarItem[] | undefined | null,
  gridStart: Date,
  gridEnd: Date,
): Map<string, number> {
  const counts = new Map<string, number>();
  if (!items?.length) return counts;

  const startMs = startOfLocalDay(gridStart).getTime();
  const endMs = startOfLocalDay(gridEnd).getTime();

  for (const item of items) {
    if (isTaxComplianceCalendarItem(item)) continue;
    if (isOngoingCalendarItem(item)) continue;
    const iso = complianceCalendarDeadlineIso(item.deadline);
    if (!iso) continue;

    const d = parseIsoDateOnlyAsLocalDate(iso);
    if (!d) continue;
    const ms = d.getTime();
    if (ms < startMs || ms > endMs) continue;

    counts.set(iso, (counts.get(iso) ?? 0) + 1);
  }

  return counts;
}

export function buildComplianceCalendarEventsByDate(
  items: ComplianceCalendarItem[] | undefined | null,
  gridStart: Date,
  gridEnd: Date,
): Map<string, DashboardCalendarEvent[]> {
  const byDate = new Map<string, DashboardCalendarEvent[]>();
  if (!items?.length) return byDate;

  const startMs = startOfLocalDay(gridStart).getTime();
  const endMs = startOfLocalDay(gridEnd).getTime();
  const todayMs = startOfLocalDay(new Date()).getTime();

  for (const item of items) {
    if (isTaxComplianceCalendarItem(item)) continue;
    if (isOngoingCalendarItem(item)) continue;
    const iso = complianceCalendarDeadlineIso(item.deadline);
    if (!iso) continue;

    const d = parseIsoDateOnlyAsLocalDate(iso);
    if (!d) continue;
    const ms = d.getTime();
    if (ms < startMs || ms > endMs) continue;

    const isRequired = item.required !== false;
    const status: DashboardCalendarEvent["status"] =
      ms < todayMs ? "overdue" : "upcoming";
    const eventType: DashboardCalendarEvent["event_type"] = isRequired
      ? "required"
      : "recommended";

    const ev: DashboardCalendarEvent = {
      date: iso,
      name: item.item || item.requirement || "Compliance deadline",
      event_type: eventType,
      status,
    };
    const list = byDate.get(iso) ?? [];
    list.push(ev);
    byDate.set(iso, list);
  }

  return byDate;
}

/** Merge BFF strip events with parallel compliance calendar events for a single day. */
export function mergeCalendarEventsForDay(
  bffEvents: DashboardCalendarEvent[],
  parallelEvents: DashboardCalendarEvent[] | undefined,
): DashboardCalendarEvent[] {
  if (!parallelEvents?.length) return bffEvents;
  if (!bffEvents.length) return parallelEvents;
  return [...bffEvents, ...parallelEvents];
}
